import React, { createContext, useState, useEffect, useContext } from 'react';
import pb from '../lib/pocketbase';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
    // Default fallback values (if nothing is in PocketBase yet)
    const [settings, setSettings] = useState({
        siteName: "Maison Cédric Millet",
        siteSlogan: "Pâtisserie Fine & Salon de Thé",
        siteLogo: "/images/logo_real.jpg",
        phone: "05 67 67 41 29",
        email: "contact@patisseriemillet.fr",
        address: "102 rue Porta, 81000 ALBI",
        googleMapsUrl: "",
        openingHours: {
            lundi: { ouvert: false, matin: "", apresMidi: "" },
            mardi: { ouvert: false, matin: "", apresMidi: "" },
            mercredi: { ouvert: false, matin: "", apresMidi: "" },
            jeudi: { ouvert: true, matin: "09:00 - 13:00", apresMidi: "14:00 - 18:30" },
            vendredi: { ouvert: true, matin: "09:00 - 13:00", apresMidi: "14:00 - 18:30" },
            samedi: { ouvert: true, matin: "09:00 - 13:00", apresMidi: "14:00 - 18:30" },
            dimanche: { ouvert: true, matin: "09:00 - 13:00", apresMidi: "14:00 - 17:00" }
        },
        clickCollectActive: true,
        heroImage: null,
        specialAlert: "",
        vacationStart: null,
        vacationEnd: null,
        vacationMessage: "",
        // Helper property calculated automatically
        isVacation: false,
        isClickCollectDisabled: false
    });

    const [loadingSettings, setLoadingSettings] = useState(true);

    const fetchSettings = async () => {
        try {
            const records = await pb.collection('settings').getList(1, 1);
            if (records.items.length > 0) {
                const data = records.items[0];

                setSettings(prev => {
                    let newSettings = { ...prev };

                    if (data.siteName) newSettings.siteName = data.siteName;
                    if (data.siteSlogan) newSettings.siteSlogan = data.siteSlogan;
                    if (data.phone) newSettings.phone = data.phone;
                    if (data.email) newSettings.email = data.email;
                    if (data.address) newSettings.address = data.address;
                    if (data.googleMapsUrl) newSettings.googleMapsUrl = data.googleMapsUrl;

                    if (data.siteLogo) {
                        newSettings.siteLogo = pb.files.getURL(data, data.siteLogo);
                    }
                    if (data.heroImage) {
                        newSettings.heroImage = pb.files.getURL(data, data.heroImage);
                    }

                    if (data.openingHours && Object.keys(data.openingHours).length > 0) {
                        newSettings.openingHours = { ...newSettings.openingHours, ...data.openingHours };
                    }

                    if (data.hasOwnProperty('clickCollectActive')) {
                        newSettings.clickCollectActive = data.clickCollectActive;
                    }

                    newSettings.specialAlert = data.specialAlert || "";
                    newSettings.vacationStart = data.vacationStart || null;
                    newSettings.vacationEnd = data.vacationEnd || null;
                    newSettings.vacationMessage = data.vacationMessage || "";

                    // Logic to check if we are currently in vacation
                    newSettings.isVacation = false;
                    if (newSettings.vacationStart && newSettings.vacationEnd) {
                        const now = new Date();
                        const start = new Date(newSettings.vacationStart);
                        const end = new Date(newSettings.vacationEnd);
                        // Add one day to end date assuming inclusiveness of the end date
                        end.setDate(end.getDate() + 1);

                        if (now >= start && now <= end) {
                            newSettings.isVacation = true;
                        }
                    }

                    newSettings.isClickCollectDisabled = !newSettings.clickCollectActive || newSettings.isVacation;

                    return newSettings;
                });
            }
        } catch (err) {
            console.error("Error fetching global settings:", err);
        } finally {
            setLoadingSettings(false);
        }
    };

    useEffect(() => {
        fetchSettings();

        // Subscribe to real-time changes so the site updates instantly if admin changes something!
        const subscribe = async () => {
            try {
                pb.collection('settings').subscribe('*', function (e) {
                    fetchSettings();
                });
            } catch (e) { console.log("Realtime subscription err", e); }
        };
        subscribe();

        return () => {
            pb.collection('settings').unsubscribe('*');
        };
    }, []);

    return (
        <SettingsContext.Provider value={{ settings, loadingSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};
